<!-- Расходы, явно связанные с открытым заказом. -->
<template>
  <section class="card order-expenses">
    <header class="order-expenses__header">
      <div>
        <h3>Расходы заказа</h3>
        <p>Здесь учитываются только расходы, у которых выбран этот заказ.</p>
      </div>
      <base-primary-button
        v-if="canCreateExpenses"
        icon="addSquare"
        @click="showAddModal = true"
      >
        Добавить расход
      </base-primary-button>
    </header>

    <base-loader v-if="isLoading" />

    <TableComponent
      v-else-if="expenses.length"
      :data="expenses"
      :columns="tableColumns"
      :ready="tableColumns.length > 0"
      @accept-expense="handleAcceptExpense"
      @row-click="editExpense"
    />

    <PaginationComponent
      v-if="pagination.pages > 1 && !isLoading"
      :page="pagination.page"
      :pages="pagination.pages"
      :series="pagination.series"
      :route-query="$route.query"
      page-param="expenses_page"
    />

    <BaseEmptyState
      v-if="!isLoading && expenses.length === 0"
      title="Расходов по заказу пока нет"
    />

    <AddExpensesModal
      v-if="showAddModal"
      :order-id="orderId"
      :trip-id="defaultTripId"
      @close="showAddModal = false"
      @expense-created="handleChanged"
    />

    <EditExpensesModal
      v-if="selectedExpenseId"
      :expense-id="selectedExpenseId"
      @close="selectedExpenseId = null"
      @expense-updated="handleChanged"
      @expense-deleted="handleChanged"
      @expense-accepted="handleExpenseAccepted"
    />

    <Teleport to="body">
      <BaseDialog
        v-if="showAcceptDialog"
        :visible="showAcceptDialog"
        title="Подтвердить расход"
        message="Вы уверены, что хотите подтвердить этот расход?"
        @action="handleAcceptDialogAction"
      />
    </Teleport>
  </section>
</template>

<script>
import AddExpensesModal from "@/components/Expense/AddExpensesModal.vue";
import EditExpensesModal from "@/components/Expense/EditExpensesModal.vue";
import PaginationComponent from "@/components/PaginationComponent.vue";
import TableComponent from "@/components/TableComponent.vue";
import { useExpenseStore } from "@/stores/expenseStore";
import { useTableConfigStore } from "@/stores/tableConfigStore";
import { can } from "@/utils/permissionUtils";

export default {
  name: "OrderExpensesList",

  components: {
    AddExpensesModal,
    EditExpensesModal,
    PaginationComponent,
    TableComponent,
  },

  props: {
    orderId: { type: [String, Number], required: true },
    trips: { type: Array, default: () => [] },
  },

  emits: ["changed", "count-change"],

  data() {
    return {
      expenses: [],
      pagination: {
        page: 1,
        pages: 1,
        series: [],
        count: 0,
        limit: 40,
      },
      tableColumns: [],
      isLoading: true,
      showAddModal: false,
      selectedExpenseId: null,
      showAcceptDialog: false,
      pendingAcceptExpenseId: null,
      expensesRequestRevision: 0,
    };
  },

  computed: {
    expenseStore() {
      return useExpenseStore();
    },
    tableConfigStore() {
      return useTableConfigStore();
    },
    canCreateExpenses() {
      return can("Expense", "create");
    },
    defaultTripId() {
      return this.trips.length === 1 ? Number(this.trips[0].id) : null;
    },
    currentPage() {
      const page = Number.parseInt(this.$route.query.expenses_page, 10);
      return Number.isNaN(page) || page < 1 ? 1 : page;
    },
  },

  watch: {
    orderId() {
      this.loadExpenses();
    },
    "$route.query.expenses_page"(newPage, oldPage) {
      if (newPage !== oldPage) this.loadExpenses();
    },
  },

  methods: {
    async loadExpenses() {
      const orderKey = String(this.orderId);
      const requestRevision = ++this.expensesRequestRevision;
      this.isLoading = true;
      try {
        const { records, pagination } = await this.expenseStore.fetchExpensesPage({
          page: this.currentPage,
          "q[order_id]": this.orderId,
        });

        if (
          requestRevision !== this.expensesRequestRevision
          || orderKey !== String(this.orderId)
        ) {
          return;
        }

        this.expenses = this.tableConfigStore.enrichRecordsWithCustomFields(
          records,
          this.tableColumns
        );
        this.pagination = pagination;
        this.$emit("count-change", pagination.count ?? records.length);
      } finally {
        if (requestRevision === this.expensesRequestRevision) {
          this.isLoading = false;
        }
      }
    },

    async loadTableConfig() {
      this.tableColumns = await this.tableConfigStore.initTableConfiguration(
        "Expense",
        "expenses",
        []
      );
    },

    editExpense(expense) {
      this.selectedExpenseId = expense.id;
    },

    handleAcceptExpense(expenseId) {
      this.pendingAcceptExpenseId = expenseId;
      this.showAcceptDialog = true;
    },

    async handleAcceptDialogAction(actionKey) {
      this.showAcceptDialog = false;

      if (actionKey !== "confirm") {
        this.pendingAcceptExpenseId = null;
        return;
      }

      const expenseId = this.pendingAcceptExpenseId;
      this.pendingAcceptExpenseId = null;

      try {
        await this.expenseStore.acceptExpense(expenseId);
        await this.loadExpenses();
      } catch {
        // Ошибка подтверждения уже показана глобальным обработчиком API.
      }
    },

    async handleExpenseAccepted() {
      await this.loadExpenses();
    },

    async handleChanged() {
      this.showAddModal = false;
      this.selectedExpenseId = null;
      await this.loadExpenses();
      this.$emit("changed");
    },
  },

  async created() {
    await this.loadTableConfig();
    await this.loadExpenses();
  },
};
</script>

<style scoped>
.order-expenses {
  display: flex;
  flex-direction: column;
  gap: var(--base-out-indent);
}

.order-expenses__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--base-out-indent);
}

.order-expenses__header h3,
.order-expenses__header p {
  margin: 0;
}

.order-expenses__header p {
  margin-top: 4px;
  color: var(--color-text-secondary, #6b7280);
  font-size: 13px;
}

@media (max-width: 767px) {
  .order-expenses__header {
    align-items: stretch;
    flex-direction: column;
  }
}
</style>
